"use client"

import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Badge,
  Row,
  Col,
} from "reactstrap"
import { Btn } from "../../../AbstractElements"
import { FaEnvelope, FaPhone, FaCalendar, FaFileAlt, FaUser } from "react-icons/fa"
import type { PersonWithAccount } from "./types"

interface PersonDetailModalProps {
  isOpen: boolean
  onClose: () => void
  person: PersonWithAccount | null
}

export default function PersonDetailModal({ isOpen, onClose, person }: PersonDetailModalProps) {
  if (!person) return null

  const getInitials = (firstName: string, lastName: string) => {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
  }

  const getRoleBadges = (account: any) => {
    const roles = []
    if (account?.is_admin) roles.push({ name: "Admin", color: "danger" })
    if (account?.is_verifier) roles.push({ name: "Verifier", color: "primary" })
    if (account?.is_registrar) roles.push({ name: "Registrar", color: "secondary" })
    if (account?.is_staff) roles.push({ name: "Staff", color: "info" })
    return roles
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>User Details</ModalHeader>
      <ModalBody>
        {/* Header with Avatar and Basic Info */}
        <div className="d-flex align-items-start mb-4">
          <div className="avatar avatar-xl me-4">
            {person.photo_url ? (
              <img
                src={person.photo_url || "/placeholder.svg"}
                alt={person.full_name}
                className="rounded-circle"
                style={{ width: "80px", height: "80px", objectFit: "cover" }}
                onError={(e) => {
                  // Fallback to initials if image fails to load
                  const target = e.target as HTMLImageElement
                  target.style.display = "none"
                  const fallback = target.nextElementSibling as HTMLElement
                  if (fallback) fallback.style.display = "flex"
                }}
              />
            ) : null}
            <div
              className="avatar-initial rounded-circle bg-primary text-white fs-4 d-flex align-items-center justify-content-center"
              style={{
                width: "80px",
                height: "80px",
                display: person.photo_url ? "none" : "flex",
              }}
            >
              {getInitials(person.first_name, person.last_name)}
            </div>
          </div>
          <div className="flex-grow-1">
            <h3 className="mb-2">{person.full_name}</h3>
            <div className="d-flex gap-2 mb-2">
              {/* <Badge color={person.status ? "success" : "secondary"}>{person.status ? "Active" : "Inactive"}</Badge> */}
              {person.account && (
                <Badge color={person.account.is_active ? "success" : "danger"}>
                  Account {person.account.is_active ? "Active" : "Inactive"}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <hr />

        {/* Personal Information */}
        <Card className="mb-4">
          <CardHeader>
            <CardTitle tag="h5" className="d-flex align-items-center">
              <FaUser className="me-2" />
              User Information
            </CardTitle>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label text-muted small">First Name</label>
                  <p className="mb-0">{person.first_name}</p>
                </div>
              </Col>
              {person.middle_name && (
                <Col md="6">
                  <div className="mb-3">
                    <label className="form-label text-muted small">Middle Name</label>
                    <p className="mb-0">{person.middle_name}</p>
                  </div>
                </Col>
              )}
              <Col md="6">
                <div className="mb-3">
                  <label className="form-label text-muted small">Last Name</label>
                  <p className="mb-0">{person.last_name}</p>
                </div>
              </Col>
            </Row>

            <Row>
              {person.email && (
                <Col md="6">
                  <div className="d-flex align-items-center mb-3">
                    <FaEnvelope className="text-muted me-2" />
                    <div>
                      <label className="form-label text-muted small mb-0">Email</label>
                      <p className="mb-0">{person.email}</p>
                    </div>
                  </div>
                </Col>
              )}
              {person.phone && (
                <Col md="6">
                  <div className="d-flex align-items-center mb-3">
                    <FaPhone className="text-muted me-2" />
                    <div>
                      <label className="form-label text-muted small mb-0">Phone</label>
                      <p className="mb-0">{person.phone}</p>
                    </div>
                  </div>
                </Col>
              )}
            </Row>

            {(person.id_doc_number || person.id_doc_type) && (
              <div className="d-flex align-items-start mb-3">
                <FaFileAlt className="text-muted me-2 mt-1" />
                <div>
                  <label className="form-label text-muted small mb-0">ID Document</label>
                  <p className="mb-0">
                    {person.id_doc_number && `Number: ${person.id_doc_number}`}
                   
                  </p>
                </div>
              </div>
            )}

            <div className="d-flex align-items-center">
              <FaCalendar className="text-muted me-2" />
              <div>
                <label className="form-label text-muted small mb-0">Created</label>
                <p className="mb-0">{new Date(person.created_at).toLocaleString()}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Account Information */}
        {person.account && (
          <Card>
            <CardHeader>
              <CardTitle tag="h5" className="d-flex align-items-center">
                <FaUser className="me-2" />
                Account Information
              </CardTitle>
            </CardHeader>
            <CardBody>
              <Row>
                <Col md="6">
                  <div className="mb-3">
                    <label className="form-label text-muted small">Account Status</label>
                    <div className="d-flex gap-2">
                      <Badge color={person.account.is_active ? "success" : "danger"}>
                        {person.account.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {/* <Badge color="secondary" outline>
                        {person.account.status}
                      </Badge> */}
                    </div>
                  </div>
                </Col>
                <Col md="6">
                  <div className="mb-3">
                    <label className="form-label text-muted small">Email Verification</label>
                    <div>
                      <Badge color={person.account.email_verified ? "success" : "warning"}>
                        {person.account.email_verified ? "Verified" : "Pending"}
                      </Badge>
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="mb-3">
                <label className="form-label text-muted small">Roles</label>
                <div className="d-flex flex-wrap gap-2">
                  {getRoleBadges(person.account).length > 0 ? (
                    getRoleBadges(person.account).map((role) => (
                      <Badge key={role.name} color={role.color}>
                        {role.name}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted small">No roles assigned</span>
                  )}
                </div>
              </div>

              <div>
                <label className="form-label text-muted small">Account Created</label>
                <p className="mb-0">{new Date(person.account.date_joined).toLocaleString()}</p>
              </div>
            </CardBody>
          </Card>
        )}
      </ModalBody>
      <ModalFooter>
        <Btn color="secondary" onClick={onClose}>
          Close
        </Btn>
      </ModalFooter>
    </Modal>
  )
}
